// 引き継ぎコードの発行／入力モーダル（docs/AUTH.md の実装順序(6)）。
//
// 🔴 **発行したコードを表示できるのは、発行した直後の1回だけ**。サーバーは指紋しか
//    保存しないので「あとで見直す」は原理的にできない。だから画面も「表示」ではなく
//    **「発行する」**と書き、控えを促す文言を必ず添える。
//
// ⚠️ 入力側（`promptTransferCode`）は**持ち主を差し替えない**。誰として入れたかを返すだけで、
//    この端末の記録をどうするか（統合の確認）は呼び出し側の仕事
//    ＝ [dashboard.ts](dashboard.ts) がパスキーのログインと同じ経路に合流させる。
import { t } from '@/platform/i18n';
import type { LoggedInPlayer } from '@/platform/types/session';
import {
  formatTransferCodeInput,
  issueTransferCode,
  redeemTransferCode,
} from '@/platform/net/transfer';
import { el, openOverlay } from './modalChrome';

/**
 * 引き継ぎコードを発行して見せる。閉じたら解決する（呼び出し側は何も受け取らない）。
 */
export function showTransferCode(playerId: string): Promise<void> {
  return new Promise((resolve) => {
    const { overlay, panel, close } = openOverlay();

    const title = el('div', 'hs-modal-title', t('portal.transfer.issueTitle'));
    const body = el('div', 'hs-modal-body', t('portal.transfer.issueBody'));
    // ⚠️ 「前のコードが無効になる」「二度と表示できない」は**押す前に**見せる。
    //    あとから伝えても取り返しがつかない類の情報。
    const warn = el('div', 'hs-modal-warn', t('portal.transfer.issueWarn'));
    const err = el('div', 'hs-modal-err', '');
    const row = el('div', 'hs-modal-row');
    const cancelBtn = el('button', 'hs-btn hs-btn-ghost', t('portal.transfer.cancel'));
    const okBtn = el('button', 'hs-btn hs-btn-primary', t('portal.transfer.issueSubmit'));
    row.append(cancelBtn, okBtn);
    panel.append(title, body, warn, err, row);

    // 🔴 発行の**通信中**は閉じさせない。ここで閉じると、モーダルは消えたのにサーバー側の発行は
    //    成立する＝「**誰も見ていないコードが作られ、前のコードだけ無効になる**」という、
    //    利用者から見て理由の分からない締め出しが起きる。
    let busy = false;

    const done = () => {
      close();
      resolve();
    };

    /** 発行できたら画面を作り替える。**この表示が唯一の受け渡し**なので、閉じる以外の道を残さない。 */
    const showCode = (code: string) => {
      title.textContent = t('portal.transfer.codeTitle');
      warn.remove();
      err.remove();
      const codeBox = el('div', 'hs-transfer-code', code);
      const note = el('div', 'hs-field-note', t('portal.transfer.codeNote'));
      body.replaceChildren(codeBox, note);

      const copyBtn = el('button', 'hs-btn hs-btn-ghost', t('portal.transfer.copy'));
      copyBtn.addEventListener('click', () => {
        // クリップボードは環境や権限で使えないことがある。使えなくても
        // コードは画面に出ている（`user-select` を効かせてある）ので、失敗しても黙って諦める。
        void navigator.clipboard
          ?.writeText(code)
          .then(() => {
            copyBtn.textContent = t('portal.transfer.copied');
          })
          .catch(() => {
            /* 手で選んで写してもらう */
          });
      });
      const closeBtn = el('button', 'hs-btn hs-btn-primary', t('portal.transfer.close'));
      closeBtn.addEventListener('click', done);
      row.replaceChildren(copyBtn, closeBtn);
    };

    const run = async (): Promise<void> => {
      busy = true;
      (okBtn as HTMLButtonElement).disabled = true;
      (cancelBtn as HTMLButtonElement).disabled = true;
      okBtn.textContent = t('portal.transfer.issueWorking');
      err.textContent = '';
      const res = await issueTransferCode(playerId);
      busy = false;
      if (res.ok) {
        showCode(res.code);
        return;
      }
      // 「この端末では発行できない」は再試行しても永久に成功しない。文言を分ける。
      err.textContent =
        res.reason === 'denied' ? t('portal.transfer.issueDenied') : t('portal.transfer.issueError');
      okBtn.textContent = t('portal.transfer.issueSubmit');
      (okBtn as HTMLButtonElement).disabled = false;
      (cancelBtn as HTMLButtonElement).disabled = false;
    };

    okBtn.addEventListener('click', () => void run());
    cancelBtn.addEventListener('click', done);
    overlay.addEventListener('mousedown', (e) => {
      if (e.target !== overlay) return;
      if (busy) return; // 発行の通信中（上の 🔴）
      // ⚠️ コードを出したあとも**背景クリックで閉じない**。写し取る前に消えると取り返しがつかない。
      if (panel.contains(warn)) done();
    });
  });
}

/**
 * 引き継ぎコードを入力してもらい、通ったプレイヤーを返す。
 * やめた場合は `null`（呼び出し側は何もしない）。
 */
export function promptTransferCode(): Promise<LoggedInPlayer | null> {
  return new Promise((resolve) => {
    const { overlay, panel, close } = openOverlay();

    const title = el('div', 'hs-modal-title', t('portal.transfer.useTitle'));
    const body = el('div', 'hs-modal-body', t('portal.transfer.useBody'));
    const label = el('label', 'hs-field-label', t('portal.transfer.useLabel'));
    const input = document.createElement('input');
    input.className = 'hs-input hs-code-input';
    input.type = 'text';
    input.placeholder = 'XXXX-XXXX-XXXX-XXXX';
    // 引き継ぎコードは英数字だけ。IME・自動大文字化・自動修正が邪魔をしないようにする。
    input.autocomplete = 'off';
    input.spellcheck = false;
    input.setAttribute('autocapitalize', 'characters');
    input.setAttribute('autocorrect', 'off');
    const err = el('div', 'hs-modal-err', '');
    const row = el('div', 'hs-modal-row');
    const cancelBtn = el('button', 'hs-btn hs-btn-ghost', t('portal.transfer.cancel'));
    const okBtn = el('button', 'hs-btn hs-btn-primary', t('portal.transfer.useSubmit'));
    row.append(cancelBtn, okBtn);
    panel.append(title, body, label, input, err, row);
    input.focus();

    // 照合の通信中は閉じさせない。ここで閉じると**セッションだけ発行された状態**で
    // 呼び出し側は何も受け取らず、統合の確認を挟めないまま端末が中途半端になる。
    let busy = false;

    const done = (player: LoggedInPlayer | null) => {
      close();
      resolve(player);
    };

    // 打ちながら `XXXX-XXXX-...` に整える（区切りを自分で打たなくてよい）。
    input.addEventListener('input', () => {
      input.value = formatTransferCodeInput(input.value);
    });

    const run = async (): Promise<void> => {
      busy = true;
      (okBtn as HTMLButtonElement).disabled = true;
      (cancelBtn as HTMLButtonElement).disabled = true;
      okBtn.textContent = t('portal.transfer.useWorking');
      err.textContent = '';
      const res = await redeemTransferCode(input.value);
      busy = false;
      if (res.ok) {
        done(res.player);
        return;
      }
      err.textContent =
        res.reason === 'malformed'
          ? t('portal.transfer.useInvalid')
          : res.reason === 'unknown'
            ? t('portal.transfer.useUnknown')
            : t('portal.transfer.useError');
      okBtn.textContent = t('portal.transfer.useSubmit');
      (okBtn as HTMLButtonElement).disabled = false;
      (cancelBtn as HTMLButtonElement).disabled = false;
      input.focus();
    };

    okBtn.addEventListener('click', () => void run());
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') void run();
    });
    cancelBtn.addEventListener('click', () => done(null));
    overlay.addEventListener('mousedown', (e) => {
      if (e.target === overlay && !busy) done(null);
    });
  });
}
